import React, { useCallback, useMemo } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import CreateClassDrawer from "./CalendarForm/ClassForm/CreateClass";
import CreateEventDrawer from "./CalendarForm/EventForm/CreateEvent";
import EditClassDrawer from "./CalendarForm/ClassForm/EditClass";
import EditEventDrawer from "./CalendarForm/EventForm/EditEvent";
import QuickEditModal from "./CalendarForm/QuickEditModal";
import { isCypress } from "../../utils/cypress";
import { useRouteUtils } from "../../context/routeUtils";

const prefix = isCypress ? "*" : "";

interface RouteParams {
  uuid: string;
}

interface CalendarActionsRouterProps {
  refetch?: () => void;
  baseUrl: string;
}

function CalendarActionsRouter({
  refetch,
  baseUrl,
}: CalendarActionsRouterProps) {
  const { hasNavigated } = useRouteUtils();
  const history = useHistory();
  const location = useLocation();

  const initialCreateValues = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return {
      initialDate: queryParams.get("initialDate") || undefined,
      initialHour: queryParams.get("initialHour") || undefined,
      endHour: queryParams.get("endHour") || undefined,
    };
  }, [location?.search]);

  const handleClose = useCallback(() => {
    if (hasNavigated) {
      history.goBack();
      return;
    }

    history.replace(baseUrl);
  }, [history, hasNavigated, baseUrl]);

  const matchCreateClass = useRouteMatch<RouteParams>(
    `${prefix}${baseUrl}/create/class`,
  );
  const matchCreateEvent = useRouteMatch<RouteParams>(
    `${prefix}${baseUrl}/create/event`,
  );
  const matchDuplicateClass = useRouteMatch<RouteParams>(
    `${prefix}${baseUrl}/duplicate/class/:uuid`,
  );
  const matchDuplicateEvent = useRouteMatch<RouteParams>(
    `${prefix}${baseUrl}/duplicate/event/:uuid`,
  );
  const matchEditClass = useRouteMatch<RouteParams>(
    `${prefix}${baseUrl}/edit/class/:uuid`,
  );
  const matchEditEvent = useRouteMatch<RouteParams>(
    `${prefix}${baseUrl}/edit/event/:uuid`,
  );

  return (
    <>
      <CreateClassDrawer
        onSuccess={() => {
          refetch?.();
          handleClose();
        }}
        visible={!!matchCreateClass}
        onClose={handleClose}
        initialDate={initialCreateValues.initialDate}
        initialHour={initialCreateValues.initialHour}
        endHour={initialCreateValues.endHour}
      />
      <CreateEventDrawer
        onSuccess={() => {
          refetch?.();
          handleClose();
        }}
        visible={!!matchCreateEvent}
        onClose={handleClose}
      />
      <CreateClassDrawer
        onSuccess={() => {
          refetch?.();
          handleClose();
        }}
        visible={!!matchDuplicateClass}
        onClose={handleClose}
        duplicateCalendarItemUuid={matchDuplicateClass?.params.uuid}
      />
      <CreateEventDrawer
        onSuccess={() => {
          refetch?.();
          handleClose();
        }}
        visible={!!matchDuplicateEvent}
        onClose={handleClose}
        duplicateCalendarItemUuid={matchDuplicateEvent?.params.uuid}
      />
      <EditClassDrawer
        visible={!!matchEditClass}
        onClose={handleClose}
        calendarItemUuid={matchEditClass?.params.uuid}
        onSuccess={() => {
          refetch?.();
          handleClose();
        }}
      />
      <EditEventDrawer
        visible={!!matchEditEvent}
        onClose={handleClose}
        calendarItemUuid={matchEditEvent?.params.uuid}
        onSuccess={() => {
          refetch?.();
          handleClose();
        }}
      />
    </>
  );
}

export default CalendarActionsRouter;
